import * as React from 'react';
import { observer } from 'mobx-react';
import { makeObservable, observable } from 'mobx';
import { Drawer } from '@shared/ui-native/Drawer';
import { Events, EventsGlobal } from '@shared/Events';
import { ReactUtils } from '@shared/ui-react/ReactUtils';
import { FormGroup } from '@shared/ui-react/UtilComponents';
import { cs } from '@shared/utils/utils-classname';
import { Button } from '@shared/ui-react/Button';
import { FiletreeSelectReact } from '@shared/ui-react/FiletreeSelectReact';
import { SimpleMeasurementsStore } from './SimpleMeasurementsStore';
import { saveVolumeApi } from '../tools/volume/utils/api';
import { Measurement, SaveVolumeApiParams } from '../../../typings/reality-capture/volumes/models';
import { KmlExport } from '../tools/KmlExport';
import { FileTreeService } from '@shared/services/FileTreeService';
import './MeasurementEditComponent.scss';

export const MeasurementEditor = {
    open(measurement: Partial<ISimpleMeasurement> | null, treeData: ITreeData | null, store: SimpleMeasurementsStore, measurementsToSave?: IMapMeasurementsSavedCoords) {
        const drawerElement = Drawer.open();
        Events.trigger('drawer-open.editor');

        ReactUtils.safeUnmountComponentAtNode(drawerElement);
        ReactUtils.safeRender(<MapMeasurementEditPanel measurement={measurement} treeData={treeData} store={store} measurementsToSave={measurementsToSave} />, drawerElement);
    },

    openSaveVolume(measurement: Measurement) {
        const drawerElement = Drawer.open();
        Events.trigger('drawer-open.editor');

        ReactUtils.safeUnmountComponentAtNode(drawerElement);
        FileTreeService.fetchFolderTree().then(tree => {
            ReactUtils.safeRender(<MapMeasurementEditPanel volumeMeasurement={measurement} treeData={tree} store={null as any} measurement={null} />, drawerElement);
        });
    },

    close() {
        Drawer.close();
        Events.trigger('drawer-close.editor');
    },
};

interface IMapMeasurementEditPanelProps {
    measurement: Partial<ISimpleMeasurement> | null;
    treeData: ITreeData | null;
    store: SimpleMeasurementsStore;
    measurementsToSave?: IMapMeasurementsSavedCoords;
    volumeMeasurement?: Measurement;
    onSuccess?: () => void;
}

@observer
class MapMeasurementEditPanel extends React.Component<IMapMeasurementEditPanelProps> {
    @observable name = this.props.measurement?.name ?? this.props.volumeMeasurement?.name ?? '';
    @observable description = this.props.measurement?.description ?? '';
    @observable folderId: number | null = this.props.measurement?.folderId ?? kuura.getRootFolderId();
    @observable folderUuid: string = kuura.getRootFolderUuid();
    @observable isPrivate = this.props.measurement?.private ?? false;
    @observable isSaving = false;
    @observable error: string | null = null;

    nameEl: HTMLInputElement | null = null;

    constructor(props: IMapMeasurementEditPanelProps) {
        super(props);
        makeObservable(this);
    }

    componentDidMount() {
        setTimeout(() => {
            this.nameEl?.focus();
        }, 100);
    }

    get isEdit() {
        return !!this.props.measurement?.uuid;
    }

    isFormValid = () => {
        if (this.props.volumeMeasurement) {
            return this.name.length > 0;
        }
        return this.name.length > 0 && this.description.length <= 255;
    };

    saveVolume = async () => {
        const m = this.props.volumeMeasurement!;
        const file = KmlExport.getFile([m.feature]);
        const params: SaveVolumeApiParams = {
            folderUuid: this.folderUuid,
            name: this.name,
            desc: this.description,
            type: m.type,
            file: file,
            fill_volume: m.fill_volume,
            cut_volume: m.cut_volume,
            baseHeight: m.baseHeight,
            tolerance: m.tolerance,
            area: m.area,
            reference: m.calcReference,
            target: m.calcTarget,
            colors: m.colors,
            failure_rate: m.failure_rate,
            highest_point: m.highest_point,
            lowest_point: m.lowest_point,
            dem_url: m.viz_surface.url,
            heatmap_url: m.viz_surface.heatmap_url,
            alpha_url: m.viz_surface.alpha_url,
            bounds: JSON.stringify(m.viz_surface.bounds),
        };
        await saveVolumeApi(params);
    };

    handleSave = async () => {
        this.isSaving = true;
        this.error = null;
        try {
            if (this.props.volumeMeasurement) {
                await this.saveVolume();
            } else {
                if (this.isEdit) {
                    await this.props.store.update(this.props.measurement!.uuid!, {
                        name: this.name,
                        description: this.description,
                        folderId: this.folderId,
                        private: this.isPrivate,
                    });
                } else {
                    await this.props.store.create({
                        name: this.name,
                        description: this.description,
                        folderId: this.folderId,
                        private: this.isPrivate,
                        measurements: this.props.measurementsToSave ?? { distance: [], area: [] },
                    });
                }
            }
            Events.trigger(EventsGlobal.MAP_MEASUREMENT_SAVED);
            MeasurementEditor.close();
        } catch (e) {
            console.error(e);
            if (this.props.volumeMeasurement) {
                this.error = __('general.http_error');
            }
        } finally {
            this.isSaving = false;
        }
    };

    render() {
        const { treeData, volumeMeasurement, measurement } = this.props;
        const isVolume = !!volumeMeasurement;
        const isSameAuthor = measurement?.authorId === kuura.getUserId();
        const canEditPrivate = !isVolume && (!this.props.measurement || isSameAuthor);

        return (
            <>
                <header className='map-measurement-editor__header'>
                    <h3>
                        {isVolume
                            ? __('js_map_measuring_tools.save_volume')
                            : this.isEdit
                            ? __('js_map_measuring_tools.edit_measurement')
                            : __('js_map_measuring_tools.save_measurement')}
                    </h3>
                </header>

                <main id='map-measurement-editor' className={cs({ 'overlay-loading': this.isSaving })}>
                    <form
                        autoComplete='off'
                        onSubmit={e => {
                            e.preventDefault();
                            this.handleSave();
                        }}
                    >
                        <FormGroup title={__('general.name')} checkable={false}>
                            <input type='text' ref={el => (this.nameEl = el)} maxLength={128} value={this.name} onChange={e => (this.name = e.currentTarget.value)} />
                            {this.name.length === 128 && (
                                <div className='ik-input-limit'>
                                    <span>{__('general.validation.character_limit_reached')}</span>
                                    <span>{this.name.length}/128</span>
                                </div>
                            )}
                            {this.name.length === 0 && <div>{__('general.validation.cannot_be_empty')}</div>}
                        </FormGroup>

                        <FormGroup title={kuura.getMessage('general.description')}>
                            <textarea name='description' value={this.description} onChange={e => (this.description = e.currentTarget.value)} />
                            {this.description.length > 255 && <div>{__('general.validation.maximum_n_field_length', 255)}</div>}
                        </FormGroup>

                        {treeData && (
                            <FormGroup title={__('general.folder')} checkable={false}>
                                <FiletreeSelectReact
                                    formFieldName={isVolume ? 'folderUuid' : 'folderId'}
                                    selectable='folder'
                                    useUuid={isVolume}
                                    treeData={treeData}
                                    selectedId={isVolume ? this.folderUuid : this.folderId}
                                    onChange={val => {
                                        if (isVolume) {
                                            this.folderUuid = val;
                                        } else {
                                            this.folderId = val ? parseInt(val) : null;
                                        }
                                    }}
                                    storageKey={isVolume ? undefined : 'MapMeasurementEditor_folderSelectTree'}
                                />
                            </FormGroup>
                        )}

                        {canEditPrivate && (
                            <div className='form-group'>
                                <label>{__('general.visibility')}</label>
                                <div style={{ display: 'flex' }} className='btn-group btn-group-block ik-button-secondary--subtle'>
                                    <Button icon='iki iki-show' onClick={() => (this.isPrivate = false)} className={cs({ active: !this.isPrivate })}>
                                        {__('general.visibility_public')}
                                    </Button>
                                    <Button icon='iki iki-hide' onClick={() => (this.isPrivate = true)} className={cs({ active: this.isPrivate })}>
                                        {__('general.visibility_private')}
                                    </Button>
                                </div>
                            </div>
                        )}
                        {this.error && <div className='form-errors'>{this.error}</div>}
                    </form>
                </main>

                <footer className='flex-spread'>
                    <button onClick={MeasurementEditor.close}>{__('general.close')}</button>
                    <Button className='ik-button-primary' onClick={this.handleSave} disabled={!this.isFormValid()} loading={this.isSaving}>
                        {__(isVolume ? 'general.save' : this.isEdit ? 'general.save' : 'general.create')}
                    </Button>
                </footer>
            </>
        );
    }
}
